import { createPlaceholderNode } from '@ui/lib/reactFlow/helpers/createPlaceholderNode'
import { BiSave } from 'react-icons/bi'
import { useShallow } from 'zustand/react/shallow'

import { Button } from '@ui/components/core'
import { NodeTypeEnum, PanelEnum } from '@ui/interfaces'
import { uuid } from '@ui/lib/utils'
import { useAuth } from '@ui/providers/AuthProvider.tsx'
import { useCodeNodeStore, useNodeAndEdgeStore, useSaveUpdateStatusStore } from '@ui/stores'

export const SaveToExistingPipeline = ({ setSaveTabClosed }: { setSaveTabClosed: () => void }) => {
  const code = useCodeNodeStore(useShallow((state) => state.code))
  const setZustandCode = useCodeNodeStore(useShallow((state) => state.setCode))

  const previouslySavedDescription = useNodeAndEdgeStore(useShallow((state) => state.getCurrentNodeKey('description')))
  const setDescription = useCodeNodeStore(useShallow((state) => state.setDescription))
  const description = useCodeNodeStore(useShallow((state) => state.description))
  const currentNodeId = useNodeAndEdgeStore(useShallow((state) => state.currentNodeId)) as string
  const updateNodeKeyValue = useNodeAndEdgeStore(useShallow((state) => state.updateNodeKeyValue))

  const setPipelineModified = useSaveUpdateStatusStore(useShallow((state) => state.setPipelineModified))
  const setSaveButtonEnabled = useSaveUpdateStatusStore(useShallow((state) => state.setSaveButtonEnabled))
  const setCurrentNodeId = useNodeAndEdgeStore(useShallow((state) => state.setCurrentNodeId))
  const setDisplayModal = useNodeAndEdgeStore(useShallow((state) => state.setDisplayModal))
  const updateNodeType = useNodeAndEdgeStore(useShallow((state) => state.updateNodeType))

  const { isDemoMode } = useAuth()

  const onSave = () => {
    setDisplayModal(PanelEnum.HIDDEN)
    updateNodeType(currentNodeId, NodeTypeEnum.CODE)
    createPlaceholderNode({ placeHolderNodeId: uuid(), currentNodeId })
    updateNodeKeyValue(currentNodeId, {
      modified: true,
      ogtype: NodeTypeEnum.CODE,
      highlight: true,
      code: code || '',
      description: description || previouslySavedDescription,
    })
    setPipelineModified(true)
    if (!isDemoMode) setSaveButtonEnabled(true)
    setCurrentNodeId(null)
    setZustandCode('')
    setDescription('')
    setSaveTabClosed()
  }

  const onDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setDescription(value)
  }

  return (
    <div className={'mt-5 flex h-[75dvh] w-full flex-col border bg-white p-3'}>
      <div className={'text-lg'}>Save code</div>
      <div className={'text-base text-gray-500'}>A code node will be added to your pipeline after saving</div>
      <div className={'mt-5'}>
        <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
          Description
        </label>
        <div className="mt-2">
          <input
            type="text"
            name="name"
            id="name"
            placeholder="A JS function"
            onChange={onDescriptionChange}
            value={description}
            data-testid={'code-save-description'}
            className="block w-full rounded-md border-0 bg-gray-100 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-400 sm:text-sm sm:leading-6"></input>
        </div>
      </div>

      <div className={'mt-2 flex w-full flex-row space-x-4'}>
        <Button variant={'primary'} onClick={onSave} disabled={description == ''} data-testid={'code-save-button'}>
          <BiSave className={'mb-0.5 mr-1.5 inline-block h-5 w-5'} />
          Save
        </Button>
        <Button
          variant={'secondary'}
          onClick={() => {
            setSaveTabClosed()
          }}
          data-testid={'code-cancel-button'}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
